
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NeighborhoodDetail } from '../../../../mocks/neighborhoodDetails';
import { neighborhoodDetails } from '../../../../mocks/neighborhoodDetails';

interface ContactCTAProps {
  neighborhood: NeighborhoodDetail;
  formUrl: string;
}

export default function ContactCTA({ neighborhood, formUrl }: ContactCTAProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: `Inquiry about ${neighborhood.name}`,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const otherNeighborhoods = neighborhoodDetails.filter((n) => n.id !== neighborhood.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: `Inquiry about ${neighborhood.name}`, message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 4000);
    }
  };

  return (
    <section id="contact" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Get in Touch</h2>
            <p className="text-gray-500 text-sm mb-8">
              Have a question about {neighborhood.name}? Want to list your business? We&apos;d love to hear from you.
            </p>

            <form
              id="neighborhood-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 cursor-pointer"
                >
                  <option>Inquiry about {neighborhood.name}</option>
                  <option>List my business</option>
                  <option>Report incorrect information</option>
                  <option>Partnership opportunity</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  maxLength={500}
                  required
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 resize-none"
                  placeholder="Tell us how we can help..."
                ></textarea>
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <i className="ri-check-line text-emerald-600"></i>
                  <p className="text-sm text-emerald-700">Message sent successfully! We&apos;ll get back to you soon.</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <i className="ri-error-warning-line text-red-600"></i>
                  <p className="text-sm text-red-700">Something went wrong. Please try again.</p>
                </div>
              )}

              <p className="text-[11px] text-gray-400">
                By submitting this form, you agree to our privacy policy. We&apos;ll never share your information.
              </p>
            </form>
          </div>

          {/* Explore Other Districts */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Explore Other Districts</h3>
            <div className="space-y-3">
              {otherNeighborhoods.map((n) => (
                <Link
                  key={n.id}
                  to={`/neighborhoods/${n.slug}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-teal-200 hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={n.heroImage}
                      alt={n.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                      {n.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{n.tagline} &middot; {n.businessCount} businesses</p>
                    <div className="flex items-center gap-1 mt-1">
                      <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                      <span className="text-xs font-semibold text-gray-700">{n.avgRating}</span>
                      <span className="text-[10px] text-gray-400">({n.totalReviews.toLocaleString()} reviews)</span>
                    </div>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-teal-500 transition-colors"></i>
                  </div>
                </Link>
              ))}
            </div>

            {/* Advertise CTA */}
            <div className="mt-6 bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-3-line text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-base mb-1">Own a Business Here?</h4>
                  <p className="text-white/80 text-xs mb-4 leading-relaxed">
                    Get featured in the {neighborhood.name} guide and reach thousands of local residents.
                  </p>
                  <Link
                    to="/advertise"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-teal-700 rounded-lg text-xs font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-megaphone-line"></i>
                    Advertise With Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
